import React, { useState } from "react";
import axios from "axios";
import gonLogo from "../assets/GON-logo.webp";
import { setUser, gapi } from "../montok/component/GlobalAPI";
import "../styles/Login.css";

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [loginType, setLoginType] = useState(""); // "level1" or "department"
  const [loginTypeSelected, setLoginTypeSelected] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [backendLoginType, setBackendLoginType] = useState(""); // For debugging

  // Format username for backend
  const formatUsername = (input) => {
    return input.trim().replace(/[<>]/g, "").substring(0, 30);
  };

  // Validate username pattern
  const validateUsername = (input) => {
    const pattern = /^[a-zA-Z0-9_. -]+$/;
    return pattern.test(input);
  };

  const handleUsernameChange = (e) => {
    const rawInput = e.target.value;
    setUsername(rawInput);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Validate inputs
    if (!username.trim()) {
      setError("Username is required");
      setIsLoading(false);
      return;
    }

    if (!validateUsername(username)) {
      setError(
        "Username can only contain letters, numbers, spaces, dots, underscores, and hyphens"
      );
      setIsLoading(false);
      return;
    }

    if (!phoneNumber.trim() || phoneNumber.length < 10) {
      setError("Valid phone number is required (10 digits)");
      setIsLoading(false);
      return;
    }

    console.log(`📱 ========== SEND OTP ATTEMPT ==========`);
    console.log(`👤 Username: ${username}`);
    console.log(`👤 Formatted Username: ${formatUsername(username)}`);
    console.log(`📞 Phone Number: ${phoneNumber}`);
    console.log(`👥 Frontend Login Type: ${loginType}`);

    // Determine backend login type based on frontend selection
    let backendLoginTypeValue;
    if (loginType === "level1") {
      // For Level 1 section - backend will determine if it's official or staff
      backendLoginTypeValue = "level1";
    } else {
      // For Department section - always department users
      backendLoginTypeValue = "department";
    }

    setBackendLoginType(backendLoginTypeValue);
    console.log(`⚙️ Backend Login Type: ${backendLoginTypeValue}`);

    try {
      const formattedUsername = formatUsername(username);
      const payload = {
        username: formattedUsername,
        phoneNumber: phoneNumber.replace(/\D/g, ""),
        loginType: backendLoginTypeValue,
      };

      console.log("📤 Payload to backend:", payload);
      console.log("🌐 Making API call to:", `${gapi}/api/send-otp`);

      const response = await axios.post(`${gapi}/api/send-otp`, payload);

      console.log("✅ OTP sent successfully:", response.data);
      setOtpSent(true);
      setError("");
    } catch (error) {
      console.error(`❌ OTP SEND FAILED:`, error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.response?.data?.details ||
        "Failed to send OTP. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (!otp.trim()) {
      setError("OTP is required");
      setIsLoading(false);
      return;
    }

    console.log(`🔐 ========== LOGIN ATTEMPT ==========`);
    console.log(`👤 User: ${username}`);
    console.log(`📞 Phone: ${phoneNumber}`);
    console.log(`🔑 OTP: ${otp}`);
    console.log(`🏷️ Login Type: ${loginType}`);
    console.log(`⚙️ Backend Login Type: ${backendLoginType}`);

    try {
      const url = `${gapi}/api/verify-otp`;
      console.log(`🌐 Making API call to: ${url}`);

      const formattedUsername = formatUsername(username);
      const cleanPhoneNumber = phoneNumber.replace(/\D/g, "");

      const payload = {
        username: formattedUsername,
        phoneNumber: cleanPhoneNumber,
        otp: otp,
        loginType: backendLoginType, // Use the backend login type
      };

      console.log("📤 Login payload to backend:", payload);

      const response = await axios.post(url, payload);

      console.log(`✅ LOGIN SUCCESSFUL:`, response.data);
      console.log(`👤 User Type: ${response.data.user?.user_type}`);
      console.log(`👤 User Data:`, response.data.user);
      console.log(`🔑 Session ID:`, response.data.sessionId);

      // ✅ STORE THE SESSION ID AND USER DATA
      setUser(response.data.user, response.data.sessionId);

      // Call the onLogin callback with updated data
      onLogin(response.data.user, response.data.sessionId);
    } catch (error) {
      console.error(`❌ LOGIN FAILED:`, error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.response?.data?.details ||
        "Invalid OTP or login failed. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginTypeChange = (selectedType) => {
    console.log(`🔄 Login type changed to: ${selectedType}`);
    setLoginType(selectedType);
    setLoginTypeSelected(true);
    setError("");
    setUsername("");
    setPhoneNumber("");
    setOtp("");
    setOtpSent(false);
    setBackendLoginType("");
  };

  const handleBackToSelection = () => {
    setLoginTypeSelected(false);
    setLoginType("");
    setUsername("");
    setPhoneNumber("");
    setOtp("");
    setOtpSent(false);
    setError("");
    setBackendLoginType("");
  };

  const handleResendOtp = () => {
    setOtpSent(false);
    setOtp("");
    setError("");
  };

  const getLoginTypeDisplay = () => {
    if (loginType === "level1") {
      return "Chief Secretary Office";
    } else if (loginType === "department") {
      return "Department Login";
    }
    return "";
  };

  const getLoginTypeDescription = () => {
    if (loginType === "level1") {
      return "Authorized Personnel Only";
    } else if (loginType === "department") {
      return "Authorized Personnel Only";
    }
    return "";
  };

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Header */}
        <div className="login-header">
          <div className="logo">
            <div className="logo-container">
              <img
                src={gonLogo}
                alt="SPEAR Nagaland Logo"
                className="logo-image"
                onError={(e) => {
                  console.error("❌ Logo failed to load:", e);
                  e.target.style.display = "none";
                  const fallback = document.querySelector(".fallback-logo");
                  if (fallback) fallback.style.display = "block";
                }}
                onLoad={() => console.log("✅ Logo loaded successfully")}
              />
              <div className="fallback-logo">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 12H15M9 16H15M9 8H15M5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
            <h1 className="logo-text">SPEAR Nagaland</h1>
          </div>
          <p className="subtitle">
            State Performance Evaluation and Analytics Repository
          </p>

          {/* Login Type Selection */}
          {!loginTypeSelected ? (
            <div className="login-type-selection">
              <h3 className="selection-title">Choose Login Type</h3>
              <div className="selection-buttons">
                <button
                  type="button"
                  className="selection-button"
                  onClick={() => handleLoginTypeChange("level1")}
                >
                  <div className="button-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <div className="button-content">
                    <div className="button-title">Chief Secretary Office</div>
                  </div>
                  <div className="button-arrow">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </button>

                <button
                  type="button"
                  className="selection-button"
                  onClick={() => handleLoginTypeChange("department")}
                >
                  <div className="button-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M16 8H8V16H16V8Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div className="button-content">
                    <div className="button-title">Department Login</div>
                  </div>
                  <div className="button-arrow">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </button>
              </div>
            </div>
          ) : (
            <div className="login-type-section">
              <div className="login-type-header">
                <button
                  type="button"
                  className="back-button"
                  onClick={handleBackToSelection}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15 18L9 12L15 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Back
                </button>
                <div className="selected-type-display">
                  <div className="selected-type-icon">
                    {loginType === "level1" ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
                          fill="currentColor"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M16 8H8V16H16V8Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="selected-type-text">
                    <div className="selected-type-title">
                      {getLoginTypeDisplay()}
                    </div>
                    <div className="selected-type-subtitle">
                      {getLoginTypeDescription()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Login Form */}
        {loginTypeSelected && (
          <>
            <form
              onSubmit={otpSent ? handleSubmit : handleSendOtp}
              className="login-form"
            >
              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={handleUsernameChange}
                  placeholder={
                    loginType === "level1"
                      ? "e.g., Sharma Kumar or Staff Name"
                      : "e.g., Department Officer"
                  }
                  className="form-input"
                  required
                  disabled={otpSent}
                  pattern="[a-zA-Z0-9_. -]+"
                  title="Only letters, numbers, spaces, dots, underscores, and hyphens are allowed"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    setPhoneNumber(value);
                  }}
                  className="form-input"
                  required
                  disabled={otpSent}
                  maxLength={10}
                  minLength={10}
                  title="Please enter a 10-digit phone number"
                  placeholder="10-digit mobile number"
                />
              </div>

              {otpSent && (
                <div className="form-group">
                  <label className="form-label">OTP</label>
                  <div className="otp-container">
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) =>
                        setOtp(e.target.value.replace(/\D/g, ""))
                      }
                      placeholder="Enter 6-digit OTP"
                      className="form-input otp-input"
                      required
                      maxLength={6}
                      minLength={6}
                      pattern="\d{6}"
                      title="Please enter a 6-digit OTP"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="resend-otp-button"
                      onClick={handleResendOtp}
                    >
                      Resend OTP
                    </button>
                  </div>
                  <div className="otp-instruction">
                    OTP sent to {phoneNumber}
                    {/* {loginType === "level1" && (
                      <span className="otp-note">
                        {" "}
                        • If staff, OTP goes to staff phone
                      </span>
                    )} */}
                  </div>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="error-icon"
                  >
                    <path
                      d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className={`login-button ${
                  isLoading ? "login-button-loading" : ""
                }`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    {otpSent ? "Verifying..." : "Sending OTP..."}
                  </>
                ) : otpSent ? (
                  "Verify & Login"
                ) : (
                  "Send OTP"
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
